<script setup>
import { computed, inject, defineProps, defineEmits, ref, unref } from 'vue';
import { VInput, VIconRender, VRadioButton, VCheckbox, VDropdown } from '../../components';
import OrderSummary from '@/modules/Signup/components/sign-up-form/OrderSummary.vue';
import { useSignUpModuleStore } from '@/stores/modules/signup/signUpStore';
import { storeToRefs } from 'pinia/dist/pinia';
import VInputCalendar from '@/modules/Signup/components/VInputCalendar.vue';
import VModal from '@/modules/Signup/components/VModal.vue';
import VButton from '@/modules/Signup/components/VButton.vue';
import { useVLoader } from '@/modules/Signup/composables/useVLoader';
import VLoader from '@/modules/Signup/components/VLoader.vue';
import { useRouter } from 'vue-router';

// Define props and emits for the component
defineProps({
    showOrderingOptionsModal: {
        type: Boolean,
        default: false,
    },
    orderingOptionsSelected: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits([
    'update:showOrderingOptionsModal',
    'update:orderingOptionsSelected',
    'selectionConfirmed',
]);

const router = useRouter();

const
    signUpStore = useSignUpModuleStore(),
    { address, optOrderingOptions, selectedOrderingOption, hasUseSiteContactDetails } = storeToRefs(signUpStore);

const { isLoading, loadingText, withLoading } = useVLoader();

const selectedOption = ref(null);

async function confirmSelection() {
    if (selectedOption.value !== null) {
        // Set the selected option in the store
        selectedOrderingOption.value = selectedOption.value;

        // When the user selects "transfer", it will redirect to the AVC page for process continuity.
        if (selectedOption.value === 'churnWithExistingNonVoice') {
            await router.push({
                name: 'signup',
                query: { from: 'details', next: 'activation-process' },
            });
            return;
        }

        // Trigger initiate order
        const { operation, loadingText } = await signUpStore.processInitiateOrder();
        const isOrderInitiated = await withLoading(operation, loadingText);

        if (!isOrderInitiated) {
            // Redirect to ErrorView
            await router.push({ name: 'error' });
            // TODO: should probably clear data if an error occurred.
            return;
        }

        // Close the modal
        emit('update:showOrderingOptionsModal', false);
        // Set the flag to indicate that ordering options have been selected
        emit('update:orderingOptionsSelected', true);
        // Emit event to signal that selection is confirmed and form can proceed
        emit('selectionConfirmed');
        // Reset selection
        selectedOption.value = null;

        await router.push({
            name: 'signup',
            query: { from: 'details', next: 'payment-method' },
        });
    }
}

const form = inject('form');

// Billing and Shipping address shares the same value, so just this is enough.
const derivedBillingAddressesOptions = computed(() => {
    const { PlaceID, FormattedAddress } = unref(address);
    return [
        { id: PlaceID, name: FormattedAddress },
        { id: 'custom', name: 'Choose another address' },
    ];
});
</script>

<template>
    <div class="tw-flex tw-items-start tw-gap-x-20">
        <div class="tw-w-3/5 tw-grid tw-grid-cols-2 tw-gap-x-6 tw-gap-y-4">
            <VInput
                v-model="form.firstname"
                label="Name"
                description="First Name"
                required
            >
                <template #icon>
                    <VIconRender :icon-src="require('@/modules/Signup/__clients/ezi/icons/user.svg')" />
                </template>
            </VInput>
            <VInput
                v-model="form.lastname"
                description="Last Name"
                required
            />
            <VInput
                v-model="form.email"
                label="Email"
                class="tw-col-span-2 tw-my-8"
                required
            >
                <template #icon>
                    <VIconRender :icon-src="require('@/modules/Signup/__clients/ezi/icons/email.svg')" />
                </template>
            </VInput>
            <VInput
                v-model="form.phone"
                label="Phone"
                required
            >
                <template #icon>
                    <VIconRender :icon-src="require('@/modules/Signup/__clients/ezi/icons/phone.svg')" />
                </template>
            </VInput>
            <VInputCalendar
                v-model="form.dob"
                label="Date of birth"
            >
                <template #icon>
                    <VIconRender :icon-src="require('@/modules/Signup/__clients/ezi/icons/calendar.svg')" />
                </template>
            </VInputCalendar>

            <!-- Billing Address -->
            <VRadioButton
                v-model="form.billing_address"
                :options="derivedBillingAddressesOptions"
                label="Billing Address"
                class="tw-col-span-2 tw-mt-16"
            />
            <!-- Show text box when "Choose another address" is selected -->
            <VInput
                v-if="form.billing_address.id === 'custom'"
                v-model="form.custom_billing_address"
                class="tw-col-span-2 tw-ml-14"
            />

            <!-- Shipping Address -->
            <VDropdown
                v-model="form.shipping_method"
                label="Shipping"
                :options="[
                    { id: 5, name: 'Australia Post' },
                ]"
                placeholder="Select Shipping Method"
            />

            <VRadioButton
                v-model="form.shipping_address"
                :options="derivedBillingAddressesOptions"
                class="tw-col-span-2 tw-mt-16"
            />
            <VInput
                v-if="form.shipping_address.id === 'custom'"
                v-model="form.custom_shipping_address"
                class="tw-col-span-2 tw-ml-14"
            />

            <VCheckbox
                v-model="form.has_existing_connection"
                label="Existing connection"
                class="tw-col-span-2 tw-mt-16"
            >
                <p>There is an existing connection at this address.</p>
                <p>I confirm that I am the authorised account holder or have the authority to transfer the service.</p>
            </VCheckbox>

            <VCheckbox
                v-model="hasUseSiteContactDetails"
                label="Use this as Site Contact Details"
                class="tw-col-span-2 tw-mt-16"
            >
                <p>
                    <strong>Note:</strong>&nbsp;
                    When checked, the details you've entered (e.g., first name, last name, etc.) will be copied
                    automatically. If unchecked, you'll be redirected to a separate form where you'll need to manually
                    enter
                    the required details.
                </p>
            </VCheckbox>
        </div>

        <OrderSummary class="tw-w-2/5" />
    </div>

    <VModal
        :modelValue="showOrderingOptionsModal"
        @update:modelValue="emit('update:showOrderingOptionsModal', $event)"
    >
        <p class="tw-text-2xl">
            Select an ordering option
        </p>

        <!--
            TODO:
                - Componentize, probably check PrimeVue if it has like an option on dropdown but exposed initially the
                options instead of clicking for the dropdown to show
                - dropdown should be disable by default, if there's no selection made, or create a prop for
                pre-selection
        -->
        <ul class="tw-list-none tw-pl-0 tw-mb-6">
            <li
                v-for="(options, optionIndex) in optOrderingOptions"
                :key="optionIndex"
                class="tw-p-2 tw-my-2 tw-border tw-rounded"
                :class="{'tw-bg-primary tw-text-white': selectedOption === optionIndex}"
                @click="selectedOption = optionIndex"
            >
                {{ options }}
            </li>
        </ul>

        <div class="tw-flex tw-justify-end tw-gap-4">
            <VButton
                label="Cancel"
                @click="emit('update:showOrderingOptionsModal', false)"
                bg-class="!tw-bg-[#ADABC6]"
            />
            <VButton
                label="Confirm Selection"
                @click="confirmSelection"
                :disabled="selectedOption === null"
            />
        </div>
    </VModal>

    <!-- Show loader during route transitions -->
    <VLoader
        v-if="isLoading"
        :text="loadingText"
    />
</template>
